/**
 * Recurring Donations Card Component
 *
 * Manages and displays recurring donation subscriptions
 */

import { FC } from 'react';
import { ArrowPathIcon, PauseIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { DashboardOverviewResponse } from '@/types/dashboard.types';

interface RecurringDonationsCardProps {
  overviewData: DashboardOverviewResponse | null;
}

export const RecurringDonationsCard: FC<RecurringDonationsCardProps> = ({ overviewData }) => {
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Recurring Donations</h3>
          <p className="text-sm text-gray-600">Manage your automatic donations</p>
        </div>
        <button className="text-sm text-primary-600 hover:text-primary-700 font-medium">
          + Add New
        </button>
      </div>

      {overviewData?.recurring_donations && overviewData.recurring_donations.length > 0 ? (
        <div className="space-y-4">
          {overviewData?.recurring_donations.map((donation) => (
            <div
              key={donation.subscription_id}
              className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <ArrowPathIcon className="h-5 w-5 text-primary-600" />
                    <h4 className="font-semibold text-gray-900">{donation.categories[0]}</h4>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        donation.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {donation.status === 'active' ? 'Active' : 'Paused'}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <p className="text-xs text-gray-500">Amount</p>
                      <p className="text-lg font-bold text-gray-900">
                        ${donation.amount.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Frequency</p>
                      <p className="text-sm font-medium text-gray-900">{donation.frequency}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Next Payment</p>
                      <p className="text-sm font-medium text-gray-900">
                        {donation.next_payment_date
                          ? new Date(donation.next_payment_date).toLocaleDateString('en-US')
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Payment Method</p>
                      <p className="text-sm font-medium text-gray-900">{donation.payment_method}</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 ml-4">
                  <button
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    title="Edit"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    className="p-2 text-gray-600 hover:text-yellow-600 hover:bg-yellow-50 rounded transition-colors"
                    title="Pause"
                  >
                    <PauseIcon className="h-4 w-4" />
                  </button>
                  <button
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Cancel"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}

          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-900">
              <span className="font-semibold">Total Monthly Commitment:</span> $
              {overviewData?.recurring_donations
                .reduce(
                  (sum, d) => sum + (d.frequency.toLowerCase() === 'monthly' ? d.amount : 0),
                  0
                )
                .toFixed(2)}
            </p>
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <ArrowPathIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-500 mb-2">No recurring donations set up</p>
          <p className="text-sm text-gray-400 mb-4">
            Set up automatic monthly donations to support the community regularly
          </p>
          <button className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium">
            <ArrowPathIcon className="h-5 w-5 mr-2" />
            Set Up Recurring Donation
          </button>
        </div>
      )}
    </div>
  );
};
